import React from 'react';

/**
 * Fallback value for Enosi brand green color.
 * The canonical source is $enosi-green in src/scss/bootstrap/_custom_variables.scss,
 * exposed as CSS custom property --enosi-green in _variable_overrides.scss.
 * This fallback is used when CSS isn't available (e.g., SSR, tests without CSS setup).
 */
const ENOSI_GREEN_FALLBACK = '#3bb667';

let cachedEnosiGreen = null;
/**
 * Gets the Enosi green color from CSS custom properties, falling back to hardcoded value.
 * @returns {string} The Enosi green color hex value
 */
function getEnosiGreen() {
  if (cachedEnosiGreen) return cachedEnosiGreen;

  if (typeof document !== 'undefined') {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--enosi-green')
      .trim();
    if (cssValue) {
      cachedEnosiGreen = cssValue;
      return cachedEnosiGreen;
    }
  }
  return ENOSI_GREEN_FALLBACK;
}

/**
 * Highlights matching text within a string by wrapping matched portions in styled spans.
 * Matching is case-insensitive, but the original casing is preserved in the output.
 * The highlighted text is bold and colored in Enosi green to stand out against bold title text.
 * @param {string} text - The full text to display
 * @param {string} filter - The term to highlight (case-insensitive matching)
 * @returns {React.ReactNode} - Text with matched portions wrapped in styled spans,
 *                              or the original text if no filter is provided
 * @example
 * // Returns: [<span key="0" style={{...}}>Sun</span>, 'shine Apartments']
 * // where style includes fontWeight: 'bold' and color: '#3bb667' (Enosi green)
 * getHighlightedText('Sunshine Apartments', 'sun');
 *
 * // Returns: [<span key="0" ...>sun</span>, 'flower ', <span key="2" ...>Sun</span>, 'day']
 * getHighlightedText('sunflower Sunday', 'sun');
 *
 * // Returns: 'Sunshine Apartments' (no highlighting when filter is empty)
 * getHighlightedText('Sunshine Apartments', '');
 */
export function getHighlightedText(text, filter) {
  if (!filter || !text) {
    return text;
  }

  // Escape special regex characters in search term
  const escapedSearchTerm = filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
  const parts = text.split(regex);

  // If no match found, return original text
  if (parts.length === 1) {
    return text;
  }

  const enosiGreen = getEnosiGreen();
  const highlightStyle = { fontWeight: 'bold', color: enosiGreen };

  return parts.map((part, index) => {
    if (part.toLowerCase() === filter.toLowerCase()) {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`${index}-${part}`} style={highlightStyle}>
          {part}
        </span>
      );
    }
    return part;
  });
}

export default getHighlightedText;
